<template>
  <CopyTrackWrapper
    type="strategy"
    :title="copyTrackerParams.title"
    :uuid="copyTrackerParams.uuid"
    :demo="copyTrackerParams.demo">
    <LibraryRedesignBanner v-if="libraryBannerProps && betaVisibility" :title="libraryBannerProps.name || ''">
      <template v-slot:breadcrumbList>
        <li v-if="libraryBannerProps.uuid"><a :href="libraryFolderBeta(libraryBannerProps.uuid)"><i class="fa fa-folder header-folder" /> {{ libraryBannerProps.name }}</a></li>
        <li v-if="libraryBannerProps.title" class="active">Strategy: {{ libraryBannerProps.title }}</li>
      </template>
    </LibraryRedesignBanner>
    <LibraryBanner v-if="libraryBannerProps && !betaVisibility" :title="libraryBannerProps.name || ''">
      <template v-slot:breadcrumbList>
        <li><router-link :to="{name: 'Library'}">Your Library</router-link></li>
        <li v-if="libraryBannerProps.uuid"><router-link :to="{name: 'StudentLibraryPage', params: {uuid: libraryBannerProps.uuid}}">{{ libraryBannerProps.name }}</router-link></li>
        <li v-if="libraryBannerProps.title" class="active">Strategy: {{ libraryBannerProps.title }}</li>
      </template>
    </LibraryBanner>

    <div class="container">
      <CopyTrackWrapper
        :title="copyTrackerParams.title"
        :uuid="copyTrackerParams.uuid"
        type="strategy_banner">
        <StrategyBanner v-if="bannerProps" v-bind="bannerProps"/>
      </CopyTrackWrapper>

      <div class="row">
        <div class="col-sm-8">
          <CopyTrackWrapper
            :title="copyTrackerParams.title"
            :uuid="copyTrackerParams.uuid"
            type="strategy_notes">
            <StrategyNotes
              :key="discardsKey"
              v-if="notesProps"
              v-bind="notesProps"
              @notesEdited="handleNotesEdit"/>
          </CopyTrackWrapper>

          <StrategyResourceList v-if="resourcesProps" v-bind="resourcesProps"/>
          <StrategyPanels v-if="implementationProps" v-bind="implementationProps"/>
          <StrategyPanels v-if="examplesProps" v-bind="examplesProps"/>
          <ResearchCitationPanels v-if="researchCitationProps" :research-citations="researchCitationProps" />
        </div>

        <div class="col-sm-4">
          <SavedStrategyLibraryPanel
            v-if="savedStrategyPanelProps"
            v-bind="savedStrategyPanelProps"
            @saveEdits="saveEdits"
            @discardEdits="discardEdits"
          />
          <ShareStrategyPanel v-if="sharedStrategyPanelProps" v-bind="sharedStrategyPanelProps"/>
        </div>
      </div>

      <StrategyRelated v-if="relatedProps" v-bind="relatedProps"/>

      <SubmitFeedback v-if="feedbackProps" v-bind="feedbackProps" />
    </div>
  </CopyTrackWrapper>
</template>

<script>
import { getSavedStrategy, putSavedStrategy } from '@/api/library.js'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'

import { strategyPageMixin } from '@/components/strategies/strategy_mixins.js'
import { authorizationCheckMixin } from '@/router/authorization_mixin'

import LibraryBanner from '@/components/library/LibraryBanner'
import LibraryRedesignBanner from '@/components/library/LibraryRedesignBanner'
import StrategyNotes from '@/components/strategies/details/StrategyNotes'
import SavedStrategyLibraryPanel from '@/components/strategies/SavedStrategyLibraryPanel'
import ShareStrategyPanel from '@/components/strategies/ShareStrategyPanel'
import { mapGetters } from 'vuex'
import { toolkitUrls } from '@/links'

export default {
  name: 'SavedStrategyPage',
  mixins: [authorizationCheckMixin, strategyPageMixin],
  components: {
    LibraryBanner,
    LibraryRedesignBanner,
    StrategyNotes,
    SavedStrategyLibraryPanel,
    ShareStrategyPanel
  },
  props: {
    uuid: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters('visibility', ['betaVisibility'])
  },
  data() {
    return {
      copyTrackerParams: {},
      libraryBannerProps: null,
      notesProps: null,
      savedStrategyPanelProps: null,
      sharedStrategyPanelProps: null,
      feedbackProps: null,

      savedStrategyData: {},
      strategyData: {},
      editedNotes: '',
      discardsKey: 0,
      ...toolkitUrls
    }
  },
  methods: {
    async fetchContent() {
      let savedStrategyData = await getSavedStrategy(this.uuid)

      await this.redirectIfUnauthorized(savedStrategyData.user_uuid, { name: 'Library' })

      this.parseStrategy(savedStrategyData)
    },
    parseStrategy(savedStrategyData) {
      this.savedStrategyData = savedStrategyData

      let strategyData = savedStrategyData.source_strategy
      this.strategyData = strategyData

      setTitle(`${strategyData.title} | UDL Strategies`)

      this.setCommonProps(Object.assign({}, strategyData, savedStrategyData), false)

      this.copyTrackerParams = {
        uuid: strategyData.uuid,
        title: strategyData.title,
        demo: strategyData.demo
      }

      this.libraryBannerProps = {
        uuid: savedStrategyData.student_uuid,
        name: savedStrategyData.student_name,
        title: strategyData.title
      }

      this.notesProps = {
        notes: savedStrategyData.notes,
        studentName: savedStrategyData.student_name,
        userName: savedStrategyData.student_name,
        collapsible: true,
        editable: true
      }
      this.discardsKey++

      this.savedStrategyPanelProps = {
        studentUuid: savedStrategyData.student_uuid,
        studentName: savedStrategyData.student_name,
        savedStrategyUuid: savedStrategyData.uuid,
        sourceStrategyUuid: strategyData.uuid,
        lastUpdatedAt: savedStrategyData.updated_at,
        saving: false,
        justSaved: false,
        unsavedChanges: false
      }

      this.sharedStrategyPanelProps = {
        uuid: savedStrategyData.uuid,
        shareState: savedStrategyData.shared,
        shareLinkState: savedStrategyData.share_link || ''
      }

      this.feedbackProps = {
        type: 'Strategy Detail Page',
        uuid: strategyData.uuid,
        title: strategyData.title
      }
    },
    handleNotesEdit(editedNotes) {
      this.editedNotes = editedNotes
      this.savedStrategyPanelProps.unsavedChanges = editedNotes !== this.savedStrategyData.notes
    },
    saveEdits() {
      let payload = Object.assign(
        { uuid: this.savedStrategyData.uuid },
        { notes: this.editedNotes }
      )

      putSavedStrategy(payload).then(data => {
        this.parseStrategy(data)
        this.savedStrategyPanelProps.justSaved = true
        setTimeout(() => {
          this.savedStrategyPanelProps.justSaved = false
        }, 5000)
      })
      this.$sendEvent('Clicked Save Changes', {
        type: CONTENT_TYPES.SAVED_STRATEGY
      })
    },
    discardEdits() {
      this.discardsKey++
      this.savedStrategyPanelProps.unsavedChanges = false
    }
  },
  eventContext() {
    return {
      saved_strategy_uuid: this.savedStrategyData.uuid,
      student_uuid: this.savedStrategyData.student_uuid,
      strategy_uuid: this.strategyData.uuid,
      strategy_title: this.strategyData.title,
      research_citations_count: this.savedStrategyData.research_citations
        ? this.savedStrategyData.research_citations.length
        : 0,
      is_library: true
    }
  },
  watch: {
    $route(to, from) {
      this.fetchContent()
    }
  },
  async created() {
    await this.fetchContent()
    this.$sendEvent(
      'Viewed Saved Strategy',
      {},
      {
        cu: this.strategyData.uuid,
        ct: this.strategyData.title
      }
    )
  }
}
</script>

<style>
</style>
